import { formatDate, translate } from '@/lib/i18n/runtime';
import type { Locale } from '@/lib/i18n/locales';

export function localDateValue(value: Date): string {
  return `${String(value.getFullYear()).padStart(4, '0')}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
}

/** Date-only choices are civil days in the browser's existing wall-clock zone.
 * Send RFC3339 bounds because the backend treats bare dates as UTC days. */
export function conversationDateBoundary(value: string | null | undefined, end = false): Date | null {
  if (!value) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const timestamp = new Date(value); return Number.isFinite(timestamp.getTime()) ? timestamp : null;
  }
  const [year, month, day] = value.split('-').map(Number), date = new Date(0);
  date.setFullYear(year, month - 1, day); date.setHours(0, 0, 0, 0);
  if (localDateValue(date) !== value) return null;
  if (end) date.setHours(23, 59, 59, 999);
  return date;
}

export function conversationDateWire(value: string | null | undefined, end = false): string | undefined {
  return conversationDateBoundary(value, end)?.toISOString();
}

export function conversationDatePresets(now = new Date()) {
  const day = (offset: number) => { const date = new Date(now); date.setDate(date.getDate() + offset); return localDateValue(date); };
  const month = (offset: number, end = false) => { const date = new Date(now); date.setDate(1); date.setMonth(date.getMonth() + offset); if (end) { date.setMonth(date.getMonth() + 1); date.setDate(0); } return localDateValue(date); };
  return [
    { id: 'today', from: day(0), to: day(0) }, { id: 'yesterday', from: day(-1), to: day(-1) },
    { id: 'week', from: day(-6), to: day(0) }, { id: 'month', from: day(-29), to: day(0) },
    { id: 'thisMonth', from: month(0), to: day(0) }, { id: 'lastMonth', from: month(-1), to: month(-1, true) },
  ];
}

export function formatConversationRange(from: string | null, to: string | null, locale: Locale, now = new Date()): string {
  const label = (value: string) => {
    const date = conversationDateBoundary(value);
    if (!date) return translate('dates.invalid', {}, 'inbox', locale);
    return formatDate(date, { day: 'numeric', month: 'short', ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}) }, locale);
  };
  if (from && to) return from === to ? label(from) : translate('dates.range', { from: label(from), to: label(to) }, 'inbox', locale);
  if (from) return translate('dates.since', { date: label(from) }, 'inbox', locale);
  if (to) return translate('dates.until', { date: label(to) }, 'inbox', locale);
  return '';
}
